import React, { useEffect, useState, useMemo } from 'react';
import { api, fmt } from '../utils/api';
import Topbar from '../components/Topbar';
import { Panel, Badge, StatusBadge, EmptyState, Spinner, MetricCard } from '../components/UI';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function totalExp(m) {
  return (parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0);
}

function parseMonthDate(m) {
  if (!m) return 0;
  const p = String(m).split(' ');
  return parseInt(p[1]||0)*12 + MONTH_NAMES.indexOf(p[0]);
}

export default function Dashboard({ user, setView, directorView, onExitDirectorView }) {
  const [declarations, setDeclarations] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [royalties, setRoyalties] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashYear, setDashYear] = useState(new Date().getFullYear());
  const [declMonth, setDeclMonth] = useState(() => {
    const now = new Date();
    return `${MONTH_NAMES[now.getMonth()]} ${now.getFullYear()}`;
  });

  useEffect(() => {
    Promise.all([api.getDeclarations(directorView), api.getFranchises(directorView), api.getRoyalties(), api.getPipeline()])
      .then(([decls, francs, roys, pipe]) => {
        setDeclarations(decls || []);
        setFranchises(francs || []);
        setRoyalties(roys || []);
        setPipeline(pipe || []);
        setLoading(false);
      });
  }, []);

  const kpis = useMemo(() => {
    let income = 0, expenses = 0;
    franchises.forEach(f => {
      (f.financials||[]).filter(m => (m.month||'').includes(String(dashYear))).forEach(m => {
        income += parseFloat(m.income)||0;
        expenses += totalExp(m);
      });
    });
    const yearRoy = royalties.filter(r => (r.month||'').includes(String(dashYear)));
    const yearRoyalty = yearRoy.reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);
    const royaltyMonths = new Set(yearRoy.map(r => r.month)).size;
    const activeBranches = franchises.filter(f => f.status !== 'closed').length;
    const monthlyAvgRoyalty = royaltyMonths > 0 ? yearRoyalty / royaltyMonths : 0;
    const avgPerBranch = royaltyMonths > 0 && activeBranches > 0 ? yearRoyalty / (royaltyMonths * activeBranches) : 0;
    return { income, expenses, profit:income-expenses, yearRoyalty, monthlyAvgRoyalty, avgPerBranch };
  }, [franchises, royalties, dashYear]);

  const trendData = useMemo(() => {
    const months = {};
    franchises.forEach(f => {
      (f.financials||[]).filter(m => (m.month||'').includes(String(dashYear))).forEach(m => {
        if (!months[m.month]) months[m.month] = { month:m.month, income:0, expenses:0, profit:0, royalty:0 };
        months[m.month].income += parseFloat(m.income)||0;
        months[m.month].expenses += totalExp(m);
        months[m.month].profit += (parseFloat(m.income)||0) - totalExp(m);
      });
    });
    royalties.filter(r => (r.month||'').includes(String(dashYear))).forEach(r => {
      if (!months[r.month]) months[r.month] = { month:r.month, income:0, expenses:0, profit:0, royalty:0 };
      months[r.month].royalty += parseFloat(r.royalty)||0;
    });
    return Object.values(months).sort((a,b) => {
      const pa=a.month.split(' '), pb=b.month.split(' ');
      return (parseInt(pa[1])*12+MONTH_NAMES.indexOf(pa[0]))-(parseInt(pb[1])*12+MONTH_NAMES.indexOf(pb[0]));
    });
  }, [franchises, royalties, dashYear]);

  // Missing declarations — same logic as old app
  // Check every active franchise, every month from its royaltyStartMonth (or Sep 2025 global default)
  // A month is NOT missing if it has a declaration OR has financials manually entered
  const missingDecls = useMemo(() => {
    const now = new Date();
    const GLOBAL_START_YR = 2025, GLOBAL_START_MO = 8; // Sep 2025
    const missing = [];

    franchises.filter(f => f.status !== 'closed').forEach(f => {
      // Determine start month for this branch
      let startYr = GLOBAL_START_YR, startMo = GLOBAL_START_MO;
      if (f.royaltyStartMonth) {
        const parts = f.royaltyStartMonth.split(' ');
        const mo = MONTH_NAMES.indexOf(parts[0]);
        const yr = parseInt(parts[1]);
        if (mo >= 0 && yr >= 2024) { startYr = yr; startMo = mo; }
      }

      const startIdx = startYr * 12 + startMo;
      const nowIdx = now.getFullYear() * 12 + now.getMonth();

      for (let idx = startIdx; idx < nowIdx; idx++) {
        const y = Math.floor(idx / 12);
        const m = idx % 12;
        const due = new Date(y, m + 1, 10);
        if (due > now) break;

        const mStr = MONTH_NAMES[m] + ' ' + y;

        // Check declaration exists
        const hasDecl = declarations.some(d => d.franchiseId === f.id && d.month === mStr);
        // Check financials manually entered
        const hasFinancials = (f.financials||[]).some(fin => fin.month === mStr);

        if (!hasDecl && !hasFinancials) {
          missing.push({ franchise: f, month: mStr });
        }
      }
    });

    // Sort most recent first
    return missing.sort((a, b) => parseMonthDate(b.month) - parseMonthDate(a.month));
  }, [franchises, declarations]);

  const pending = declarations.filter(d => d.status === 'pending');
  const editRequests = declarations.filter(d => d.editRequested);

  // Top branches by royalty - use location as branch name
  const royaltyByBranch = {};
  royalties.forEach(r => {
    const franchise = franchises.find(f => f.id === r.franchiseId || f.id === r.franchise_id);
    const branchLabel = franchise?.location || franchise?.name || r.franchiseName || '—';
    royaltyByBranch[branchLabel] = (royaltyByBranch[branchLabel]||0) + (parseFloat(r.royalty)||0);
  });
  const topBranches = Object.entries(royaltyByBranch).sort((a,b) => b[1]-a[1]).slice(0,6);
  const maxRoyalty = topBranches[0]?.[1] || 1;

  const today = new Date().toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Dashboard" /><Spinner /></div>;

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Dashboard" subtitle={today} actions={
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <div style={{ display:'flex', gap:4 }}>
            <button className="btn btn-sm btn-secondary" onClick={() => setDashYear(y=>y-1)}>‹</button>
            <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)', minWidth:40, textAlign:'center', lineHeight:'30px' }}>{dashYear}</span>
            <button className="btn btn-sm btn-secondary" onClick={() => setDashYear(y => y<new Date().getFullYear()?y+1:y)}>›</button>
          </div>
          {onExitDirectorView && (
            <button className="btn btn-sm btn-secondary" onClick={onExitDirectorView}>← Exit Director View</button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setView('branches')}>
            <i className="ti ti-plus" style={{ fontSize:13 }} /> New Branch
          </button>
        </div>
      } />

      <div style={{ padding:24, flex:1 }}>
        {/* KPI rows */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
          <MetricCard label={`Total Revenue ${dashYear}`} value={fmt(kpis.income)} icon="ti-trending-up" />
          <MetricCard label={`Total Expenses ${dashYear}`} value={fmt(kpis.expenses)} icon="ti-trending-down" color="var(--red)" />
          <MetricCard label={`Net Profit ${dashYear}`} value={fmt(kpis.profit)} icon="ti-coin" color={kpis.profit>=0?'var(--green)':'var(--red)'} />
          <MetricCard label="Active Branches" value={franchises.filter(f=>f.status!=='closed').length} icon="ti-building-store" color="var(--navy)" />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
          <MetricCard label={`Yearly Royalty ${dashYear}`} value={fmt(kpis.yearRoyalty)} icon="ti-coin" color="var(--amber)" />
          <MetricCard label="Monthly Avg Royalty" value={fmt(kpis.monthlyAvgRoyalty)} icon="ti-chart-bar" color="var(--amber)" />
          <MetricCard label="Avg Per Branch/Month" value={fmt(kpis.avgPerBranch)} icon="ti-building-store" color="var(--amber)" />
        </div>

        {/* Trend chart */}
        {trendData.length > 1 && (
          <div className="card" style={{ padding:20, marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)', marginBottom:12, letterSpacing:'0.05em' }}>PORTFOLIO TREND {dashYear}</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(28,25,96,0.05)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize:10, fill:'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize:10, fill:'var(--text-tertiary)' }} axisLine={false} tickLine={false} tickFormatter={v=>`£${Math.round(v/1000)}k`} />
                <Tooltip formatter={(v,n)=>[fmt(v),n]} contentStyle={{ fontSize:12, borderRadius:8, border:'1px solid var(--border)' }} />
                <Legend wrapperStyle={{ fontSize:11 }} />
                <Line type="monotone" dataKey="income" name="Income" stroke="var(--navy)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="var(--red)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="profit" name="Profit" stroke="var(--green)" strokeWidth={2.5} dot={{ fill:'var(--green)', r:3 }} />
                <Line type="monotone" dataKey="royalty" name="Royalty Paid" stroke="var(--amber)" strokeWidth={2} dot={{ fill:'var(--amber)', r:3 }} strokeDasharray="5 3" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          {/* Pending approvals */}
          <Panel title="Pending Approvals" icon="ti-checks" action={
            <button className="btn btn-secondary btn-sm" onClick={() => setView('approvals')}>View all →</button>
          }>
            {[...editRequests.slice(0,2), ...pending.slice(0,2)].length === 0
              ? <EmptyState icon="ti-check" message="No pending approvals" />
              : [...editRequests.slice(0,2), ...pending.slice(0,2)].map(d => (
                <div key={d.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer' }} onClick={() => setView('approvals')}>
                  <div style={{ width:30, height:30, borderRadius:'var(--r-md)', background:d.editRequested?'var(--amber-bg)':'var(--blue-badge-bg)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <i className={`ti ${d.editRequested?'ti-pencil':'ti-file-check'}`} style={{ fontSize:14, color:d.editRequested?'var(--amber)':'var(--blue)' }} />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--navy)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {d.editRequested?'Edit request':'Declaration'} — {d.branchLocation||d.franchiseName}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{d.month}</div>
                  </div>
                  <StatusBadge status={d.status} />
                </div>
              ))
            }
          </Panel>

          {/* Top branches */}
          <Panel title="Top Branches by Royalty" icon="ti-chart-bar" action={
            <button className="btn btn-secondary btn-sm" onClick={() => setView('royalties')}>View all →</button>
          }>
            <div style={{ padding:'12px 16px' }}>
              {topBranches.length === 0
                ? <EmptyState icon="ti-coin" message="No royalty data" />
                : topBranches.map(([name, val], i) => (
                  <div key={name} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)', width:90, textAlign:'right', flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</div>
                    <div style={{ flex:1, height:8, background:'var(--blue-bg)', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ height:'100%', borderRadius:4, width:`${(val/maxRoyalty)*100}%`, background: i===0?'var(--navy)':i<3?'var(--navy-light)':'#7573cc' }} />
                    </div>
                    <div style={{ fontSize:11, fontWeight:600, color:'var(--navy)', width:60, textAlign:'right', flexShrink:0, fontFamily:'DM Mono,monospace' }}>{fmt(val)}</div>
                  </div>
                ))
              }
            </div>
          </Panel>

          {/* Missing declarations alert */}
          {missingDecls.length > 0 && (
            <Panel title={`Missing Declarations (${missingDecls.length})`} icon="ti-alert-triangle" action={
              <button className="btn btn-secondary btn-sm" onClick={() => setView('declarations')}>View All →</button>
            }>
              {missingDecls.slice().sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month)).slice(0,8).map((m,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 16px', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--red)', flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--navy)' }}>
                      {m.franchise.location || m.franchise.name}
                      {m.franchise.location && <span style={{ fontSize:11, color:'var(--text-tertiary)', marginLeft:6 }}>({m.franchise.name})</span>}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{m.month}</div>
                  </div>
                  <Badge type="red">Missing</Badge>
                </div>
              ))}
              {missingDecls.length > 8 && <div style={{ padding:'8px 16px', fontSize:11, color:'var(--text-tertiary)' }}>+{missingDecls.length-8} more — click View All to see all</div>}
            </Panel>
          )}

          {/* Pipeline */}
          <Panel title="Pipeline Activity" icon="ti-git-branch" action={
            <button className="btn btn-secondary btn-sm" onClick={() => setView('pipeline')}>View all →</button>
          }>
            {pipeline.slice(0,4).map(p => {
              const progress = [p.ilaSignedFranchisee,p.ilaSignedInvestor,p.contractSigned,p.bankAccountOpened,p.companyCreated].filter(Boolean).length;
              const statusColor = p.status==='active'?'green':p.status==='signed'?'blue':'navy';
              return (
                <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer' }} onClick={() => setView('pipeline')}>
                  <div style={{ width:30, height:30, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'var(--navy)', flexShrink:0 }}>
                    {(p.companyName||'??').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--navy)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.companyName}</div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{progress}/5 steps · {p.location||'—'}</div>
                  </div>
                  <Badge type={statusColor}>{p.status}</Badge>
                </div>
              );
            })}
            {pipeline.length === 0 && <EmptyState icon="ti-git-branch" message="No pipeline entries" />}
          </Panel>
        </div>

        {/* Monthly Declarations Submission Status */}
        <div className="card" style={{ marginTop:16 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <i className="ti ti-file-check" style={{ fontSize:15, color:'var(--navy)' }} />
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>Declaration Submissions</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <button onClick={() => {
                const parts = declMonth.split(' ');
                const mo = MONTH_NAMES.indexOf(parts[0]);
                const yr = parseInt(parts[1]);
                const newMo = mo === 0 ? 11 : mo - 1;
                const newYr = mo === 0 ? yr - 1 : yr;
                setDeclMonth(`${MONTH_NAMES[newMo]} ${newYr}`);
              }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18, padding:'0 4px' }}>‹</button>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)', minWidth:90, textAlign:'center' }}>{declMonth}</span>
              <button onClick={() => {
                const parts = declMonth.split(' ');
                const mo = MONTH_NAMES.indexOf(parts[0]);
                const yr = parseInt(parts[1]);
                const newMo = mo === 11 ? 0 : mo + 1;
                const newYr = mo === 11 ? yr + 1 : yr;
                setDeclMonth(`${MONTH_NAMES[newMo]} ${newYr}`);
              }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18, padding:'0 4px' }}>›</button>
            </div>
          </div>
          {(() => {
            const activeFranchises = franchises.filter(f => {
              if (f.status === 'closed') return false;
              if (!f.royaltyStartMonth) return true;
              return parseMonthDate(f.royaltyStartMonth) <= parseMonthDate(declMonth);
            });
            const monthDecls = declarations.filter(d => d.month === declMonth);
            const submitted = activeFranchises.filter(f => monthDecls.some(d => d.franchiseId === f.id));
            const notSubmitted = activeFranchises.filter(f => !monthDecls.some(d => d.franchiseId === f.id));
            return (
              <div>
                {/* Summary bar */}
                <div style={{ padding:'10px 16px', background:'rgba(28,25,96,0.02)', borderBottom:'1px solid var(--border)', display:'flex', gap:20 }}>
                  <span style={{ fontSize:12 }}>Total: <strong>{activeFranchises.length}</strong></span>
                  <span style={{ fontSize:12, color:'var(--green)' }}>✓ Submitted: <strong>{submitted.length}</strong></span>
                  <span style={{ fontSize:12, color:'var(--red)' }}>✗ Missing: <strong>{notSubmitted.length}</strong></span>
                  {activeFranchises.length > 0 && (
                    <span style={{ fontSize:12, color:'var(--navy)' }}>
                      {Math.round(submitted.length/activeFranchises.length*100)}% complete
                    </span>
                  )}
                </div>
                <table className="table">
                  <thead><tr>
                    <th>BRANCH</th><th>COMPANY</th><th>STATUS</th><th>SUBMITTED</th><th>PAYMENT</th>
                  </tr></thead>
                  <tbody>
                    {activeFranchises.sort((a,b) => {
                      const aHas = monthDecls.some(d=>d.franchiseId===a.id);
                      const bHas = monthDecls.some(d=>d.franchiseId===b.id);
                      if (aHas !== bHas) return aHas ? -1 : 1;
                      return (a.location||a.name).localeCompare(b.location||b.name);
                    }).map(f => {
                      const decl = monthDecls.find(d => d.franchiseId === f.id);
                      const statusMap = { approved:'green', pending:'amber', rejected:'red' };
                      return (
                        <tr key={f.id}>
                          <td style={{ fontWeight:500, fontSize:13 }}>{f.location || f.name}</td>
                          <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{f.location ? f.name : '—'}</td>
                          <td>
                            {decl
                              ? <Badge type={statusMap[decl.status]||'navy'}>{decl.status}</Badge>
                              : <Badge type="red">Not Submitted</Badge>
                            }
                          </td>
                          <td style={{ fontSize:11, color:'var(--text-tertiary)' }}>
                            {decl?.submittedAt ? new Date(decl.submittedAt).toLocaleDateString('en-GB',{day:'numeric',month:'short'}) : '—'}
                          </td>
                          <td>
                            {decl?.paymentType === 'submit_and_pay'
                              ? <Badge type={decl.paymentStatus==='confirmed'?'green':'amber'}>{decl.paymentStatus==='confirmed'?'✓ Paid':'Pending'}</Badge>
                              : decl ? <Badge type="navy">Save Only</Badge> : '—'
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
